import type { ScheduleEventId, ScheduleId } from '../types'

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const legacyId = (id: ScheduleId | ScheduleEventId, resource: string) => {
  if (!Number.isSafeInteger(id) || Number(id) <= 0) {
    throw new TypeError(`${resource} must use a positive Mango integer ID`)
  }

  return id
}

export const isAssetUuid = (id: unknown): id is string =>
  typeof id === 'string' && UUID_PATTERN.test(id)

const assetUuid = (id: ScheduleId | ScheduleEventId, resource: string) => {
  if (!isAssetUuid(id)) {
    throw new TypeError(`${resource} must use an Asset UUID`)
  }

  return id
}

/**
 * Schedule API endpoints
 */
export const SCHEDULE_ENDPOINTS = {
  LIST: '/app/v1/schedules/',
  DETAIL: (id: ScheduleId) => `/app/v1/schedules/${legacyId(id, 'Schedule')}/`,
  CREATE: '/app/v1/schedules/',
  UPDATE: (id: ScheduleId) => `/app/v1/schedules/${legacyId(id, 'Schedule')}/`,
  DELETE: (id: ScheduleId) => `/app/v1/schedules/${legacyId(id, 'Schedule')}/`,
  INVALIDATE_CACHE: (legacyScheduleId: number) =>
    `/app/v1/schedules/${legacyId(legacyScheduleId, 'Schedule')}/invalidate-cache/`,
  EVENTS_LIST: (scheduleId: ScheduleId) =>
    `/app/v1/schedules/${legacyId(scheduleId, 'Schedule')}/events/`,
  EVENTS_CREATE: (scheduleId: ScheduleId) =>
    `/app/v1/schedules/${legacyId(scheduleId, 'Schedule')}/events/`,
  EVENTS_DETAIL: (scheduleId: ScheduleId, eventId: ScheduleEventId) =>
    `/app/v1/schedules/${legacyId(scheduleId, 'Schedule')}/events/${legacyId(eventId, 'Schedule Event')}/`,
  EVENTS_UPDATE: (scheduleId: ScheduleId, eventId: ScheduleEventId) =>
    `/app/v1/schedules/${legacyId(scheduleId, 'Schedule')}/events/${legacyId(eventId, 'Schedule Event')}/`,
  EVENTS_DELETE: (scheduleId: ScheduleId, eventId: ScheduleEventId) =>
    `/app/v1/schedules/${legacyId(scheduleId, 'Schedule')}/events/${legacyId(eventId, 'Schedule Event')}/`,
  // Preset holidays
  HOLIDAYS_LIST: '/schedule_holidays/',
} as const

export const ASSET_SCHEDULE_HOLIDAYS_ENDPOINT =
  '/assets/v1/schedule-holidays' as const

export const ASSET_SCHEDULE_ENDPOINTS = {
  LIST: '/assets/v1/schedules',
  DETAIL: (id: ScheduleId) =>
    `/assets/v1/schedules/${assetUuid(id, 'Schedule')}`,
  CREATE: '/assets/v1/schedules',
  UPDATE: (id: ScheduleId) =>
    `/assets/v1/schedules/${assetUuid(id, 'Schedule')}`,
  DELETE: (id: ScheduleId) =>
    `/assets/v1/schedules/${assetUuid(id, 'Schedule')}`,
  // The Asset invalidate-cache compatibility route remains legacy-ID addressed.
  INVALIDATE_CACHE: (legacyScheduleId: number) =>
    `/assets/v1/schedules/${legacyId(legacyScheduleId, 'Schedule')}/invalidate-cache`,
  EVENTS_LIST: (scheduleId: ScheduleId) =>
    `/assets/v1/schedules/${assetUuid(scheduleId, 'Schedule')}/events`,
  EVENTS_CREATE: (scheduleId: ScheduleId) =>
    `/assets/v1/schedules/${assetUuid(scheduleId, 'Schedule')}/events`,
  EVENTS_DETAIL: (scheduleId: ScheduleId, eventId: ScheduleEventId) =>
    `/assets/v1/schedules/${assetUuid(scheduleId, 'Schedule')}/events/${assetUuid(eventId, 'Schedule Event')}`,
  EVENTS_UPDATE: (scheduleId: ScheduleId, eventId: ScheduleEventId) =>
    `/assets/v1/schedules/${assetUuid(scheduleId, 'Schedule')}/events/${assetUuid(eventId, 'Schedule Event')}`,
  EVENTS_DELETE: (scheduleId: ScheduleId, eventId: ScheduleEventId) =>
    `/assets/v1/schedules/${assetUuid(scheduleId, 'Schedule')}/events/${assetUuid(eventId, 'Schedule Event')}`,
  HOLIDAYS_LIST: ASSET_SCHEDULE_HOLIDAYS_ENDPOINT,
} as const

/**
 * Query keys for TanStack Query
 */
export const SCHEDULE_QUERY_KEYS = {
  all: ['schedules'] as const,
  lists: () => [...SCHEDULE_QUERY_KEYS.all, 'list'] as const,
  list: (params: Record<string, unknown>) =>
    [...SCHEDULE_QUERY_KEYS.lists(), params] as const,
  details: () => [...SCHEDULE_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: ScheduleId) => [...SCHEDULE_QUERY_KEYS.details(), id] as const,
  events: (scheduleId: ScheduleId) =>
    [...SCHEDULE_QUERY_KEYS.detail(scheduleId), 'events'] as const,
  eventsList: (scheduleId: ScheduleId, params?: Record<string, unknown>) =>
    [...SCHEDULE_QUERY_KEYS.events(scheduleId), 'list', params] as const,
  eventDetail: (scheduleId: ScheduleId, eventId: ScheduleEventId) =>
    [...SCHEDULE_QUERY_KEYS.events(scheduleId), eventId] as const,
} as const
